import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { formatCurrency } from "../../dp/utils/format";

import {
  RgbPackaging,
  RgbStore,
  RgbSummary,
  downloadRgbMovementsExcelApi,
  downloadRgbSummaryExcelApi,
  getRgbPackagingsApi,
  getRgbStoresApi,
  getRgbSummaryApi,
} from "../api/rgbApi";
import { RGB_HOME_PAGE } from "../routes";
import { currentMonth, formatInt, formatPercent, monthRange, shiftMonth, todayIso } from "../rgbFormat";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Rgb.css";

/** Multi-seleção simples com `<select multiple>` — vazio = todos. */
function MultiSelect<T extends { id: string }>(props: {
  id: string;
  label: string;
  items: T[];
  getLabel: (item: T) => string;
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { id, label, items, getLabel, value, onChange } = props;
  return (
    <div className="rgbFormGroup">
      <label htmlFor={id}>
        {label} {value.length ? `(${value.length})` : "(todos)"}
      </label>
      <select
        id={id}
        className="rgbSelect"
        multiple
        style={{ height: 96, paddingTop: 6 }}
        value={value}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions).map((o) => o.value))}
      >
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {getLabel(item)}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function RgbReportPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(RGB_HOME_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [stores, setStores] = useState<RgbStore[]>([]);
  const [packagings, setPackagings] = useState<RgbPackaging[]>([]);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const [packagingIds, setPackagingIds] = useState<string[]>([]);
  const initial = monthRange(currentMonth());
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);

  const [data, setData] = useState<RgbSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"summary" | "movements" | null>(null);

  useEffect(() => {
    if (!token) return;
    Promise.all([getRgbStoresApi(), getRgbPackagingsApi(true)])
      .then(([st, pk]) => {
        setStores(st);
        setPackagings(pk);
      })
      .catch((error) => toast.error(error instanceof Error ? error.message : "Erro ao carregar filtros."));
  }, [token]);

  const filters = useMemo(
    () => ({ startDate, endDate, storeIds, packagingIds }),
    [startDate, endDate, storeIds, packagingIds],
  );

  const search = useCallback(async () => {
    if (!startDate || !endDate) {
      toast.error("Informe o período.");
      return;
    }
    setLoading(true);
    try {
      setData(await getRgbSummaryApi(filters));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao carregar o relatório.");
    } finally {
      setLoading(false);
    }
  }, [filters, startDate, endDate]);

  useEffect(() => {
    if (token) search();
    // Busca inicial com o mês corrente; depois, só pelo botão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setMonth = (month: string) => {
    const r = monthRange(month);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
  };

  const exportExcel = async (kind: "summary" | "movements") => {
    setExporting(kind);
    try {
      if (kind === "summary") await downloadRgbSummaryExcelApi(filters);
      else await downloadRgbMovementsExcelApi(filters);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao exportar.");
    } finally {
      setExporting(null);
    }
  };

  const saldo = data?.config.controleSaldo ?? false;
  const cols = saldo ? 8 : 3;
  const grand = useMemo(() => {
    if (!data) return null;
    const perdas = data.lojas.map((l) => l.perdaEstimadaTotal).filter((v): v is number => v !== null);
    return {
      refugado: data.lojas.reduce((a, l) => a + l.refugadoTotal, 0),
      perda: perdas.length ? perdas.reduce((a, b) => a + b, 0) : null,
    };
  }, [data]);
  const wholeMonth = startDate.endsWith("-01") && monthRange(startDate.slice(0, 7)).endDate === endDate;

  if (!token) return null;

  return (
    <div className="rgbLayout">
      <main className="rgbMain">
        <div className="rgbContainer">
          <EcoPageHeader
            crumbs={[{ label: "Vasilhame RGB", onClick: () => navigate(RGB_HOME_PAGE()) }, { label: "Relatório" }]}
            title="Relatório de vasilhame RGB"
            subtitle="Consolidado por loja e vasilhame, com perda estimada e meta."
            onBack={goBack}
            backLabel="Voltar para o RGB"
          />

          <div className="rgbControls">
            <MultiSelect
              id="rgbStores"
              label="Lojas"
              items={stores}
              getLabel={(s) => s.nomeFantasia}
              value={storeIds}
              onChange={setStoreIds}
            />
            <MultiSelect
              id="rgbPackagings"
              label="Vasilhames"
              items={packagings}
              getLabel={(p) => (p.ativo ? p.nome : `${p.nome} (inativo)`)}
              value={packagingIds}
              onChange={setPackagingIds}
            />
            <div className="rgbFormGroup">
              <label htmlFor="rgbStart">De</label>
              <input
                id="rgbStart"
                className="rgbInput"
                type="date"
                max={todayIso()}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="rgbFormGroup">
              <label htmlFor="rgbEnd">Até</label>
              <input id="rgbEnd" className="rgbInput" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          <div className="rgbControls">
            <button className="rgbBtn rgbBtn--secondary" onClick={() => setMonth(currentMonth())}>
              Mês atual
            </button>
            <button className="rgbBtn rgbBtn--secondary" onClick={() => setMonth(shiftMonth(currentMonth(), -1))}>
              Mês anterior
            </button>
            <button className="rgbBtn rgbBtn--primary" disabled={loading} onClick={search}>
              {loading ? "Buscando..." : "Buscar"}
            </button>
            <button className="rgbBtn rgbBtn--secondary" disabled={exporting !== null} onClick={() => exportExcel("summary")}>
              {exporting === "summary" ? "Exportando..." : "Excel resumo"}
            </button>
            <button className="rgbBtn rgbBtn--secondary" disabled={exporting !== null} onClick={() => exportExcel("movements")}>
              {exporting === "movements" ? "Exportando..." : "Excel apontamentos"}
            </button>
          </div>

          {!wholeMonth && data?.config.metaRefugoMensalPorLoja !== null && data?.config.metaRefugoMensalPorLoja !== undefined ? (
            <div className="rgbNotice">
              A meta mensal só é avaliada quando o período é exatamente um mês calendário.
            </div>
          ) : null}

          <div className="rgbTableWrapper">
            <table className="rgbTable">
              <thead>
                <tr>
                  <th>Loja / Vasilhame</th>
                  {saldo ? (
                    <>
                      <th className="is-num">Recebido</th>
                      <th className="is-num">Devolvido CDD</th>
                    </>
                  ) : null}
                  <th className="is-num">Refugado</th>
                  {saldo ? (
                    <>
                      <th className="is-num">Ajustes</th>
                      <th className="is-num">Saldo</th>
                      <th className="is-num">% refugo</th>
                    </>
                  ) : null}
                  <th className="is-num">Perda estimada</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={cols} className="rgbEmpty">
                      Carregando...
                    </td>
                  </tr>
                ) : !data || data.lojas.length === 0 ? (
                  <tr>
                    <td colSpan={cols} className="rgbEmpty">
                      Nenhum apontamento no período.
                    </td>
                  </tr>
                ) : (
                  <>
                    {data.lojas.map((loja) => (
                      <React.Fragment key={loja.store.id}>
                        <tr className="is-group">
                          <td>
                            {loja.store.nomeFantasia}
                            {loja.store.estado ? <span className="rgbMuted"> · {loja.store.estado}</span> : null}{" "}
                            {loja.acimaDaMetaMensal || loja.acimaDaMetaPercentual ? (
                              <span className="rgbBadge rgbBadge--red">Acima da meta</span>
                            ) : null}
                          </td>
                          {saldo ? (
                            <>
                              <td className="is-num">{formatInt(loja.itens.reduce((a, i) => a + i.recebido, 0))}</td>
                              <td className="is-num">{formatInt(loja.itens.reduce((a, i) => a + i.devolvido, 0))}</td>
                            </>
                          ) : null}
                          <td className="is-num">{formatInt(loja.refugadoTotal)}</td>
                          {saldo ? (
                            <>
                              <td className="is-num">{formatInt(loja.itens.reduce((a, i) => a + i.ajustes, 0))}</td>
                              <td className="is-num">{formatInt(loja.itens.reduce((a, i) => a + (i.saldo ?? 0), 0))}</td>
                              <td className="is-num">{formatPercent(loja.percentualRefugo)}</td>
                            </>
                          ) : null}
                          <td className="is-num">{formatCurrency(loja.perdaEstimadaTotal)}</td>
                        </tr>
                        {loja.itens.map((i) => (
                          <tr key={`${loja.store.id}-${i.packaging.id}`}>
                            <td style={{ paddingLeft: 32 }}>{i.packaging.nome}</td>
                            {saldo ? (
                              <>
                                <td className="is-num">{formatInt(i.recebido)}</td>
                                <td className="is-num">{formatInt(i.devolvido)}</td>
                              </>
                            ) : null}
                            <td className="is-num">{formatInt(i.refugado)}</td>
                            {saldo ? (
                              <>
                                <td className="is-num">{formatInt(i.ajustes)}</td>
                                <td className="is-num">{formatInt(i.saldo)}</td>
                                <td className="is-num">—</td>
                              </>
                            ) : null}
                            <td className="is-num">{formatCurrency(i.perdaEstimada)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                    {grand ? (
                      <tr className="is-total">
                        <td>Total geral</td>
                        {saldo ? <td colSpan={2} /> : null}
                        <td className="is-num">{formatInt(grand.refugado)}</td>
                        {saldo ? <td colSpan={3} /> : null}
                        <td className="is-num">{formatCurrency(grand.perda)}</td>
                      </tr>
                    ) : null}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
